/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2024, Zumper
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { BREAKPOINT_MOBILE } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import { logger } from '@zcode/utils'
import type { ErrorBoundaryProps } from 'react-error-boundary'

const fallbackStyle = css`
  height: 900px;
  @media (max-width: ${BREAKPOINT_MOBILE}) {
    height: 400px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for PopularCities lazy loadable section', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for PopularCities lazy loadable section', error)
}

const loadFn = /* #__LOADABLE__ */ async () => await import('./PopularCities')

const handleReload = async () => await PopularCitiesLazyLoadableSection.reload()

const options = {
  errorFallback: () => (
    <ErrorFallback
      css={fallbackStyle}
      message="Error loading popular cities"
      onClickReload={handleReload}
    />
  ),

  loadingFallback: () => <LoadingFallback css={fallbackStyle} name="popular cities" />,
  onRenderError,
  onLoadingError,
}

export const PopularCitiesLazyLoadableSection = createLazyLoadableSection(loadFn, options)
